import { canv } from "@/canv/global";
import { createBlobFromString } from "@/canv/utils";
import { eventEmitter } from "@/eventEmitter";
import type {
    TimerEntity,
    TimerEventUpdateType,
    TimerUpdatePayload,
} from "@/timerController/types";
import {
    assignTimerUpdate,
    createTimerBuildEvent,
    pauseTimerPropertyEvent,
    resetTimerPropertyEvent,
    restartTimerPropertyEvent,
    resumeTimerPropertyEvent,
    updateDurationTimerPropertyEvent,
} from "@/timerController/utils";

const getDateNow = (): string => new Date().toISOString();

const DEFAULT_TIMER_DURATION = 30;
const MS_PER_SECOND = 1000;

class TimerController {
    timer: TimerEntity | null = null;

    dispatchTimer() {
        if (this.timer) {
            eventEmitter.dispatch("timer.update", {
                duration: this.timer.duration,
                startedAt: this.timer.startedAt,
                pausedAt: this.timer.pausedAt,
                updatedAt: this.timer.updatedAt,
                estimatedStopTime: this.timer.estimatedStopTime,
                remainingAfterPause: this.timer.remainingAfterPause,
            });
        }
    }
    setTimer = (timer: TimerEntity) => {
        if (!this.timer) {
            this.timer = timer;
            this.dispatchTimer();
        }
    };

    deleteTimer = (timer: TimerEntity) => {
        const data = {
            type: "event",
            data: [{ type: "build", action: "delete", entities: [timer] }],
        };
        const blob = createBlobFromString(JSON.safeStringify(data));
        if (canv.intercom.socket) {
            canv.intercom.socket.send(blob);
        }
    };

    updateTimer(data: TimerUpdatePayload, type: TimerEventUpdateType) {
        if (!this.timer) return;
        const fieldsByType: Record<TimerEventUpdateType, (keyof TimerUpdatePayload)[]> = {
            pause: ["pausedAt", "remainingAfterPause", "updatedAt"],
            resume: [
                "duration",
                "estimatedStopTime",
                "pausedAt",
                "remainingAfterPause",
                "startedAt",
                "updatedAt",
            ],
            updateDuration: ["duration", "estimatedStopTime", "updatedAt", "remainingAfterPause"],
            reset: [
                "duration",
                "estimatedStopTime",
                "pausedAt",
                "remainingAfterPause",
                "startedAt",
                "updatedAt",
            ],
            restart: [
                "duration",
                "estimatedStopTime",
                "pausedAt",
                "remainingAfterPause",
                "startedAt",
                "updatedAt",
            ],
        };

        const updates = fieldsByType[type];
        assignTimerUpdate(this.timer, data, updates);
        this.dispatchTimer();
    }

    createWithStart(duration: number) {
        const startedAt = getDateNow();
        const finalDuration = duration || DEFAULT_TIMER_DURATION;

        const startedAtMs = new Date(startedAt).getTime();

        const estimatedStopTime = new Date(
            startedAtMs + finalDuration * MS_PER_SECOND,
        ).toISOString();

        const data = {
            duration: finalDuration,
            startedAt: startedAt,
            updatedAt: startedAt,
            estimatedStopTime: estimatedStopTime,
            pausedAt: null,
            remainingAfterPause: null,
        };
        canv.intercom.eventMessage(createTimerBuildEvent(data));
    }

    pause() {
        if (!this.timer) return;
        const pausedAt = getDateNow();
        const remainingAfterPause = this.timer.estimatedStopTime
            ? Math.max(
                  0,
                  Math.ceil(
                      (Date.parse(this.timer.estimatedStopTime) - Date.parse(pausedAt)) /
                          MS_PER_SECOND,
                  ),
              )
            : null;

        const data = {
            UID: this.timer.UID,
            updatedAt: getDateNow(),
            pausedAt: getDateNow(),
            remainingAfterPause: remainingAfterPause,
        };
        canv.intercom.eventMessage(pauseTimerPropertyEvent(data));
    }

    resume() {
        if (!this.timer) return;
        const now = getDateNow();
        if (this.timer.remainingAfterPause == null) return;

        const estimatedStopTime = new Date(
            Date.parse(now) + this.timer.remainingAfterPause * MS_PER_SECOND,
        ).toISOString();

        const data = {
            UID: this.timer.UID,
            updatedAt: now,
            pausedAt: null,
            estimatedStopTime: estimatedStopTime,
            remainingAfterPause: null,
        };
        canv.intercom.eventMessage(resumeTimerPropertyEvent(data));
    }

    updateDuration(addedTime: number) {
        if (!this.timer) return;
        const now = getDateNow();
        const newDuration = this.timer.duration + addedTime;

        let estimatedStopTime: string | null = null;
        let remainingAfterPause: number | null = this.timer.remainingAfterPause;

        if (this.timer.startedAt) {
            const startedAtMs = Date.parse(this.timer.startedAt);

            //активный, пересчитываем время окончание
            if (this.timer.estimatedStopTime) {
                estimatedStopTime = new Date(
                    startedAtMs + newDuration * MS_PER_SECOND,
                ).toISOString();
            }
            //если на паузе, пересчитываем оставшееся время
            if (this.timer.pausedAt && remainingAfterPause !== null) {
                remainingAfterPause = remainingAfterPause + addedTime;
            }
        }

        const data = {
            UID: this.timer.UID,
            duration: newDuration,
            estimatedStopTime: estimatedStopTime,
            remainingAfterPause: remainingAfterPause,
            updatedAt: now,
        };
        canv.intercom.eventMessage(updateDurationTimerPropertyEvent(data));
    }

    reset() {
        if (!this.timer) return;
        const data = {
            UID: this.timer.UID,
            duration: 0,
            startedAt: null,
            estimatedStopTime: null,
            pausedAt: null,
            remainingAfterPause: null,
            updatedAt: getDateNow(),
        };
        canv.intercom.eventMessage(resetTimerPropertyEvent(data));
    }

    restart(duration: number) {
        if (!this.timer) return;
        const now = getDateNow();
        const finalDuration = duration || DEFAULT_TIMER_DURATION;
        const nowMs = new Date(now).getTime();
        const estimatedStopTime = new Date(nowMs + finalDuration * MS_PER_SECOND).toISOString();
        const data = {
            UID: this.timer.UID,
            duration: finalDuration,
            startedAt: now,
            estimatedStopTime: estimatedStopTime,
            pausedAt: null,
            remainingAfterPause: null,
            updatedAt: now,
        };
        canv.intercom.eventMessage(restartTimerPropertyEvent(data));
    }
}

export default new TimerController();
